
import { Component, Vue } from 'vue-property-decorator';
import VendorReviewCard from '@/components/purchase-order/review/VendorReviewCard.vue';
import UnassignedReviewCard from '@/components/purchase-order/review/UnassignedReviewCard.vue';
import POCreatedBanner from '@/components/purchase-order/review/POCreatedBanner.vue';
import PORecentlyCreated from '@/components/purchase-order/PORecentlyCreated.vue';
import { purchaseList, PURCHASE_LIST_UPDATED } from '@/collections/purchase_list';
import { EventBus } from '@/eventBus';
import { createPOForSupplier } from '@/utils/purchase-order/createPOForSupplier';
import { StoreLocationList } from '@/collections/store_locations';
import { SupplierList } from '@/collections/suppliers';
import { TaxTypeList } from '@/collections/tax_types';
import { TaxTypeModel } from '@/models/tax_type';
import { SupplierModel } from '@/models/supplier';
import type { PurchaseListGroup } from '@/types/purchase-list';
import {
  fetchInventoryByVariantIds,
  type InventoryByVariantId,
} from '@/utils/purchase-order/fetchInventoryByVariantIds';
import {
  taxTypeToSeed,
  taxPercentToEdit,
  expectedDeliveryFromLeadTime,
  type ReviewLineEdits,
  type SeededTax,
} from '@/utils/purchase-order/reviewLineEdits';

interface LocationOption { id: number; name: string; }
interface SupplierOption { id: number; name: string; }
interface CreatePayload {
  group: PurchaseListGroup;
  locationId: number;
  expectedDeliveryDate: string;
}

@Component({
  // Explicit kebab keys: Vue 2 kebab-cases a PascalCase key with consecutive
  // capitals to `p-o-...` (e.g. POCreatedBanner -> p-o-created-banner), so the
  // template tags `<po-created-banner>` / `<po-recently-created>` would NOT
  // resolve under the bare identifier keys. Register the kebab names directly.
  components: {
    VendorReviewCard,
    UnassignedReviewCard,
    'po-created-banner': POCreatedBanner,
    'po-recently-created': PORecentlyCreated,
  },
})
export default class PurchaseOrderReview extends Vue {
  public purchaseListRef = purchaseList;
  public locations: LocationOption[] = [];
  public availableSuppliers: SupplierOption[] = [];
  public recentlyCreated: Array<{ id: number; poNumber: string; supplierName: string; status: string }> = [];
  public creatingSupplierIds: Record<number, boolean> = {};
  public inventoryByVariantId: InventoryByVariantId = {};
  public inventoryLoading = false;
  public lineEdits: ReviewLineEdits = {};
  public taxSeedBySupplier: Record<number, SeededTax> = {};
  // Per-vendor expected-delivery default (today + supplier lead time), resolved
  // on mount in the same pass as the tax seed. '' when the supplier has no
  // (positive) lead time configured.
  public expectedDeliveryBySupplier: Record<number, string> = {};
  public bannerPO: { id: number; poNumber: string; supplierName: string } | null = null;

  private storeLocationList = new StoreLocationList();
  private supplierListCollection = new SupplierList();
  private taxTypeList: TaxTypeList = new TaxTypeList();

  public async mounted(): Promise<void> {
    await this.purchaseListRef.fetch();
    // Tax types must be loaded before per-vendor seed resolution.
    await this.taxTypeList.fetch().catch((e: any) => console.warn('Tax types unavailable:', e));
    await Promise.all([
      this.loadLocations(),
      this.loadAvailableSuppliers(),
      this.refreshInventoryMap(),
      this.resolveTaxSeeds(),
    ]);
  }

  public seedFor(group: PurchaseListGroup): SeededTax {
    return this.taxSeedBySupplier[Number(group.supplierId)] ?? {};
  }

  public defaultExpectedDeliveryFor(group: PurchaseListGroup): string {
    return this.expectedDeliveryBySupplier[Number(group.supplierId)] ?? '';
  }

  public onSupplierSkuChange(payload: { item: { id: number }; value: string }): void {
    this.patchEdit(payload.item.id, { supplierSku: payload.value });
  }

  public onUnitCostChange(payload: { item: { id: number }; unitCost: number | undefined }): void {
    this.patchEdit(payload.item.id, { unitCost: payload.unitCost });
  }

  public onTaxPercentChange(payload: { item: { id: number }; percent: number | undefined }): void {
    this.patchEdit(payload.item.id, taxPercentToEdit(payload.percent));
  }

  public onViewBanner(): void {
    if (this.bannerPO) { this.$router.push(`/purchase-orders/${this.bannerPO.id}`); }
  }

  private async refreshInventoryMap(): Promise<void> {
    this.inventoryLoading = true;
    try {
      const ids: number[] = [];
      for (const group of Object.values(this.purchaseListRef.groups)) {
        for (const item of group.items) {
          const id = Number(item.variantId);
          if (Number.isFinite(id) && id > 0) { ids.push(id); }
        }
      }
      this.inventoryByVariantId = await fetchInventoryByVariantIds(ids);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load live inventory for the Purchase List', err);
      EventBus.$emit('show-snackbar', {
        message: 'Could not load live inventory for the Purchase List.',
        color: 'error',
      });
      this.inventoryByVariantId = {};
    } finally {
      this.inventoryLoading = false;
    }
  }

  public get hasAnyItems(): boolean {
    return Object.keys(this.purchaseListRef.groups).length > 0;
  }

  public get vendorCount(): number {
    return Object.keys(this.purchaseListRef.groups).length;
  }

  public get itemCount(): number {
    return Object.values(this.purchaseListRef.groups)
      .reduce((n, g) => n + g.items.length, 0);
  }

  public get unassignedGroup(): PurchaseListGroup | null {
    const g = this.purchaseListRef.groups.unassigned;
    return g && g.items.length > 0 ? g : null;
  }

  public get vendorGroups(): Record<string, PurchaseListGroup> {
    const all = this.purchaseListRef.groups;
    const out: Record<string, PurchaseListGroup> = {};
    for (const key of Object.keys(all)) {
      if (key !== 'unassigned') { out[key] = all[key]; }
    }
    return out;
  }

  public async loadLocations(): Promise<void> {
    try {
      await this.storeLocationList.fetch();
      this.locations = this.storeLocationList.items.map((l) => ({ id: l.id, name: l.name }));
    } catch {
      this.locations = [];
    }
  }

  public async loadAvailableSuppliers(): Promise<void> {
    try {
      await this.supplierListCollection.fetch();
      this.availableSuppliers = this.supplierListCollection.items
        .filter((s) => s.id != null)
        .map((s) => ({ id: s.id as number, name: s.name }));
    } catch {
      this.availableSuppliers = [];
    }
  }

  public isCreating(group: PurchaseListGroup): boolean {
    const id = Number(group.supplierId);
    return !!this.creatingSupplierIds[id];
  }

  public async onCreate(payload: CreatePayload): Promise<void> {
    const supplierId = Number(payload.group.supplierId);
    const supplierName = payload.group.supplierName ?? '';
    if (this.creatingSupplierIds[supplierId]) { return; }
    this.$set(this.creatingSupplierIds, supplierId, true);
    try {
      // Fetch the supplier on demand so we can read its default_tax_type_id.
      // Hard-fail if this fetch errors — creating a PO without tax data when
      // the supplier may have a configured default would silently match the
      // bug we are fixing in this change.
      const supplier = new SupplierModel({ id: supplierId });
      try {
        await supplier.fetch();
      } catch {
        EventBus.$emit('show-snackbar', {
          message: `Failed to load supplier ${supplierName}; PO not created.`,
          color: 'error',
        });
        return;
      }

      const seededTax = taxTypeToSeed(this.resolveDefaultTaxType(supplier)) ?? {};
      const groupEdits: ReviewLineEdits = {};
      for (const it of payload.group.items) {
        if (this.lineEdits[it.id]) { groupEdits[it.id] = this.lineEdits[it.id]; }
      }

      const po = await createPOForSupplier({
        supplierId,
        supplierName,
        locationId: Number(payload.locationId),
        poNumber: '',
        orderDate: new Date().toISOString().slice(0, 10),
        expectedDeliveryDate: payload.expectedDeliveryDate || undefined,
        items: payload.group.items,
        status: 'draft',
        currencyCode: payload.group.currencyCode,
        extra: {
          shipping: 0,
          shippingTaxRate: 0,
          discount: 0,
          discountType: 'fixed',
          adjustment: 0,
          adjustmentNote: '',
        },
        seededTax,
        lineEdits: groupEdits,
      });

      await purchaseList.clearSupplier(supplierId);
      await this.purchaseListRef.fetch();
      await this.refreshInventoryMap();
      for (const it of payload.group.items) { this.$delete(this.lineEdits, it.id); }
      EventBus.$emit(PURCHASE_LIST_UPDATED);

      this.recentlyCreated.unshift({
        id: po.id!,
        poNumber: po.poNumber,
        supplierName,
        status: 'draft',
      });
      this.bannerPO = { id: po.id!, poNumber: po.poNumber, supplierName };
      EventBus.$emit('show-snackbar', { message: `${supplierName} PO created.`, color: 'success' });
    } catch {
      EventBus.$emit('show-snackbar', { message: `Failed to create PO for ${supplierName}`, color: 'error' });
    } finally {
      this.$delete(this.creatingSupplierIds, supplierId);
    }
  }

  public onCancel(): void {
    // Per-card cancel only clears local state inside the card (handled there).
    // No page-level action needed.
  }

  public async onAssigned(payload: { supplier: { id: number; name: string }; count: number }): Promise<void> {
    await this.purchaseListRef.fetch();
    await this.refreshInventoryMap();
    EventBus.$emit('show-snackbar', {
      message: `Assigned ${payload.count} items to ${payload.supplier.name}`,
      color: 'success',
    });
  }

  public async onAssignFailed(payload: { supplier: { id: number; name: string }; succeeded: number; total: number }): Promise<void> {
    await this.purchaseListRef.fetch();
    await this.refreshInventoryMap();
    EventBus.$emit('show-snackbar', {
      message: `Assigned ${payload.succeeded} of ${payload.total} items to ${payload.supplier.name}. Retry the rest.`,
      color: 'error',
    });
  }

  public onViewRecent(id: number): void {
    this.$router.push(`/purchase-orders/${id}`);
  }

  private async resolveTaxSeeds(): Promise<void> {
    const entries = Object.values(this.vendorGroups);
    await Promise.allSettled(entries.map(async (group) => {
      const supplierId = Number(group.supplierId);
      if (!Number.isFinite(supplierId) || supplierId <= 0) { return; }
      try {
        const supplier = new SupplierModel({ id: supplierId });
        await supplier.fetch();
        const seed = taxTypeToSeed(this.resolveDefaultTaxType(supplier)) ?? {};
        this.$set(this.taxSeedBySupplier, supplierId, seed);
        // Reuse the same fetched supplier — no second request — to pre-fill the
        // expected-delivery date from its lead time (today + leadTime days).
        // Read `supplier.leadTime` typed (not `as any`) so tsc guards the field
        // name against a future model rename.
        const orderDate = new Date().toISOString().slice(0, 10);
        const eta = expectedDeliveryFromLeadTime(orderDate, supplier.leadTime);
        this.$set(this.expectedDeliveryBySupplier, supplierId, eta);
      } catch {
        this.$set(this.taxSeedBySupplier, supplierId, {});
        this.$set(this.expectedDeliveryBySupplier, supplierId, '');
      }
    }));
  }

  private patchEdit(id: number, p: Partial<ReviewLineEdits[number]>): void {
    this.$set(this.lineEdits, id, { ...this.lineEdits[id], ...p });
  }

  private resolveDefaultTaxType(supplier: SupplierModel | null): TaxTypeModel | null {
    if (!supplier) { return null; }
    // Try the supplier's own default first. options.financial may store the id
    // as a string in the JSON blob, so use loose equality (matches the
    // convention in PurchaseOrderCreate.vue#resolveSupplierDefaultTaxType).
    const supplierDefaultId = (supplier as any).options?.financial?.default_tax_type_id;
    if (supplierDefaultId) {
      const found = this.taxTypeList.items.find((t: TaxTypeModel) => t.id == supplierDefaultId);
      if (found) { return found; }
    }
    // Fall back to the merchant-wide default.
    return this.taxTypeList.items.find((t: TaxTypeModel) => t.isDefault) || null;
  }
}
